import React from 'react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import classNames from 'classnames';
import styles from './ImageSliderMobile.module.scss';

type SliderImage = {
    url?: string;
    alt?: string;
    isLivePreview?: boolean;
    renderPreview?: () => ReactNode;
};

type Props = {
    images: SliderImage[];
};

const selectInputsTrigger = (state: GiftPdpState) => {
    const inputs = state.project.inputs;
    return inputs.ids
        .map(id => {
            const input = inputs.entities[id];
            return input ? `${id}:${JSON.stringify(input.value)}` : '';
        })
        .join('|');
};

const renderImageContent = (
    image: SliderImage,
    imgClassName: string,
    previewClassName: string,
    altFallback: string
) => {
    if (image.isLivePreview && image.renderPreview) {
        return <div className={previewClassName}>{image.renderPreview()}</div>;
    }
    if (image.url) {
        return (
            <img
                crossOrigin='anonymous'
                src={image.url}
                alt={image.alt || altFallback}
                className={imgClassName}
            />
        );
    }
    return null;
};

export const ImageSliderMobile = ({ images }: Props) => {
    const [selectedIndex, setSelectedIndex] = useState(images.findIndex(img => img.isLivePreview));
    const inputsTrigger = useSelector(selectInputsTrigger);
    const previousTriggerRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (selectedIndex >= images.length) {
            setSelectedIndex(Math.max(0, images.length - 1));
        }
    }, [images.length, selectedIndex]);

    useEffect(() => {
        if (previousTriggerRef.current === undefined) {
            previousTriggerRef.current = inputsTrigger;
            return;
        }

        if (inputsTrigger !== previousTriggerRef.current) {
            previousTriggerRef.current = inputsTrigger;

            const livePreviewIndex = images.findIndex(img => img.isLivePreview);
            if (livePreviewIndex !== -1 && selectedIndex !== livePreviewIndex) {
                setSelectedIndex(livePreviewIndex);
            }
        }
    }, [inputsTrigger, images, selectedIndex]);

    if (images.length === 0) {
        return null;
    }

    const selectedImage = images[selectedIndex];

    return (
        <div className={styles.imageSlider}>
            <Grid direction='column' gap='s' className={styles.container}>
                <Cell className={styles.mainImageRow}>
                    <div className={styles.mainImageContainer}>
                        {renderImageContent(
                            selectedImage,
                            styles.mainImage,
                            styles.mainPreview,
                            'Preview'
                        )}
                    </div>
                </Cell>

                <Cell className={styles.thumbnailRow}>
                    <div className={styles.thumbnailScroller}>
                        {images.map((image, index) => (
                            <button
                                key={index}
                                className={classNames(styles.thumbnail, {
                                    [styles.thumbnailActive]: index === selectedIndex
                                })}
                                onClick={() => setSelectedIndex(index)}
                            >
                                {renderImageContent(
                                    image,
                                    styles.thumbnailImage,
                                    styles.thumbnailPreview,
                                    `Preview ${index + 1}`
                                )}
                            </button>
                        ))}
                    </div>
                </Cell>
            </Grid>
        </div>
    );
};